import React, { useState } from "react";
import {
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import styles from "./ContactDetails.module.css";
import Logo from "../components/Logo";
import SideBackground from "../components/SideBackground";
import NotificationSnackbar from "../components/NotificationSnackbar";
import useNotification from "../hooks/useNotification";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useAuthApi } from "../hooks/useAuthApi";

export default function ConsumerResetPassword() {
  const { notification, showNotification, hideNotification } =
    useNotification();
  const { consumerResetPassword, loading, error, clearError } = useAuthApi();
  const [form, setForm] = useState({
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState({
    password: false,
    confirmPassword: false,
  });
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
    if (error) {
      clearError();
    }
  };

  const validatePassword = (password) => {
    const rules = [
      { test: password.length >= 8, message: "at least 8 characters long" },
      { test: /[a-z]/.test(password), message: "a lower case letter" },
      { test: /[A-Z]/.test(password), message: "an upper case letter" },
      { test: /\d/.test(password), message: "a number" },
      {
        test: /[^a-zA-Z0-9]/.test(password),
        message: "a special character (e.g &!^%&*)",
      },
    ];
    const failedRules = rules.filter((rule) => !rule.test);
    if (failedRules.length > 0) {
      return `Password format not valid`;
    }
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({ password: "", confirmPassword: "" });
    clearError();

    const passwordError = validatePassword(form.password);
    if (passwordError) {
      setErrors({ ...errors, password: passwordError });
      return;
    }
    if (form.password !== form.confirmPassword) {
      setErrors({ ...errors, confirmPassword: "Passwords do not match" });
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const resetToken = params.get("token");
    if (!resetToken) {
      showNotification("Reset token missing.", "error");
      return;
    }

    const result = await consumerResetPassword({
      token: resetToken,
      newPassword: form.password,
    });

    if (result) {
      setSuccess(true);
      showNotification("Password has been set successfully.", "success");
      setTimeout(() => {
        window.location.href = "/";
      }, 2000);
    } else {
      showNotification("Invalid or Token Expired", "error");
    }
  };

  return (
    <div className={styles.contactDetailsRoot}>
      <div className={styles.contactDetailsLeft}>
        <div className={styles.contactDetailsLogoWrap}>
          <Logo />
        </div>
        <form
          className={styles.contactDetailsForm}
          onSubmit={handleSubmit}
          style={{ marginTop: errors.password ? 60 : 10 }}
        >
          <h2>Reset Password</h2>
          <h4
            style={{
              textAlign: "center",
              fontWeight: 600,
              margin: "0px",
              marginTop: "-10px",
              color: "#888",
            }}
          >
            Password
          </h4>
          <div
            style={{
              fontSize: "13px",
              width: "100%",
              textAlign: "center",
              marginBottom: 12,
              color: "#666",
            }}
          >
            <p>
              To help keep your account secure, please choose a password that is
              not
              <br />
              easily guessed.
            </p>
          </div>
          <div
            style={{
              width: "100%",
              maxWidth: 260,
              display: "flex",
              flexDirection: "column",
              margin: "0 auto",
            }}
          >
            <div style={{ width: "100%", marginBottom: 8 }}>
              <h4
                style={{
                  fontWeight: 600,
                  margin: "-10px 0px 0px 20px",
                  color: "#888",
                }}
              >
                Password must:
              </h4>
              <ul
                style={{
                  textAlign: "left",
                  margin: 0,
                  paddingLeft: 20,
                  fontSize: "13px",
                  fontWeight: 500,
                  width: "100%",
                }}
              >
                <li
                  style={{
                    color: !(form.password.length >= 8)
                      ? "#888"
                      : "var(--color-success)",
                  }}
                >
                  Be at least 8 characters long
                </li>
                <li
                  style={{
                    color: !form.password.match(/[a-z]/)
                      ? "#888"
                      : "var(--color-success)",
                  }}
                >
                  Contain a lowercase letter
                </li>
                <li
                  style={{
                    color: !form.password.match(/[A-Z]/)
                      ? "#888"
                      : "var(--color-success)",
                  }}
                >
                  Contain an uppercase letter
                </li>
                <li
                  style={{
                    color: !form.password.match(/[0-9]/)
                      ? "#888"
                      : "var(--color-success)",
                  }}
                >
                  Contain a number
                </li>
                <li
                  style={{
                    color: !form.password.match(/[^a-zA-Z0-9]/)
                      ? "#888"
                      : "var(--color-success)",
                  }}
                >
                  Contain a special character (e.g &amp;!^%&amp;*)
                </li>
              </ul>
            </div>
            <TextField
              type={showPassword.password ? "text" : "password"}
              label="Password"
              name="password"
              value={form.password}
              onChange={handleChange}
              required
              error={!!errors.password}
              inputProps={{ maxLength: 64 }}
              sx={{ mt: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() =>
                        setShowPassword({
                          ...showPassword,
                          password: !showPassword.password,
                        })
                      }
                      edge="end"
                    >
                      {showPassword.password ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              type={showPassword.confirmPassword ? "text" : "password"}
              label="Confirm Password"
              name="confirmPassword"
              value={form.confirmPassword}
              onChange={handleChange}
              required
              error={!!errors.confirmPassword}
              inputProps={{ maxLength: 64 }}
              sx={{ mt: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() =>
                        setShowPassword({
                          ...showPassword,
                          confirmPassword: !showPassword.confirmPassword,
                        })
                      }
                      edge="end"
                    >
                      {showPassword.confirmPassword ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                mt: 2,
                width: "100%",
                backgroundColor: "var(--color-primary)",
                "&:hover": { backgroundColor: "var(--color-secondary)" },
              }}
            >
              {loading ? "Resetting..." : "Continue"}
            </Button>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "100%",
                maxWidth: 260,
              }}
            >
              {errors.password && (
                <Typography
                  sx={{
                    color: "#ff1d1d",
                    mt: 2,
                    fontSize: 14,
                    padding: 1,
                    border: "2px solid",
                    borderColor: "#ff1d1d",
                    borderRadius: 1,
                    backgroundColor: "#fff",
                    textAlign: "center",
                    width: "100%",
                    maxWidth: 200,
                  }}
                >
                  {errors.password}
                </Typography>
              )}
              {errors.confirmPassword && (
                <Typography
                  sx={{
                    color: "#ff1d1d",
                    mt: 2,
                    fontSize: 14,
                    padding: 1,
                    border: "2px solid",
                    borderColor: "#ff1d1d",
                    borderRadius: 1,
                    backgroundColor: "#fff",
                    textAlign: "center",
                    width: "100%",
                    maxWidth: 200,
                  }}
                >
                  {errors.confirmPassword}
                </Typography>
              )}
            </div>
          </div>
        </form>
      </div>
      <SideBackground>
        <div className={styles.contactDetailsBgOverlay}></div>
      </SideBackground>
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={hideNotification}
      />
    </div>
  );
}
